/**
 * PlatformAssistantContext
 *
 * Holds the assistant panel's open/closed state and its remembered position.
 * Mounted once above the router (App.tsx) so navigating between the educator
 * layout and the studio never remounts it - the conversation outlives the page.
 *
 * While the panel is docked on screen it puts `assistant-docked` on the
 * document root, which sets --assistant-dock-inset: the page layouts shorten
 * their inline end by it, so the work never sits under the panel. Overlays
 * keep opening from the screen edge, over it.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';

import type { EntityChange, EntityRef } from '@/core/changes/_generated';
import { useAuth } from '@/modules/auth';
import type { TutorState } from '@/modules/live-chat/tutorState';
import type { ChatAttachment } from '@/shared/components/agent-chat/types';

import {
  clearAssistantStorage,
  readPanelState,
  writePanelState,
} from '../assistantStorage';

/** Width of the assistant panel - must match CSS variable --educator-assistant-panel-width */
export const ASSISTANT_PANEL_WIDTH = 400;

/** A question posted from the page, carried to the open panel. */
export interface AssistantSeed {
  text: string;
  nonce: number;
}

export type AssistantPlacement = 'floating' | 'docked';

/** What opens a conversation: an existing session (null for a new one of its own), then a first message to send, with its files. */
export interface AssistantSessionRequest {
  sessionId: string | null;
  message: string | null;
  attachments?: ChatAttachment[];
}

/** A conversation the panel should open, once. */
export interface AssistantSessionStart extends AssistantSessionRequest {
  nonce: number;
}

/** The embed slot is the whole page: the panel keeps its header, with Minimize for leaving the page. */
export interface AssistantPageEmbed {
  onMinimize: () => void;
}

/** An entity's key in the set of what Lamma is working on. */
export const agentWorkKey = (ref: EntityRef): string => `${ref.type}:${ref.id}`;

const NO_WORK: ReadonlySet<string> = new Set();

interface PlatformAssistantContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  placement: AssistantPlacement;
  setPlacement: (placement: AssistantPlacement) => void;
  /** The panel is docked and on screen right now. */
  isDocked: boolean;
  setDockedOnScreen: (docked: boolean) => void;
  /** The pending session start, consumed by the panel. */
  sessionStart: AssistantSessionStart | null;
  /** Open the panel on a session, sending the message once it is open. */
  startSession: (start: AssistantSessionRequest) => void;
  clearSessionStart: () => void;
  /** The session the panel holds, reported by the panel. */
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  /** Where a spoken exchange stands, reported by the panel; null while the panel is not in voice mode. */
  voiceState: TutorState | null;
  setVoiceState: (state: TutorState | null) => void;
  /** What Lamma created or changed in the turn running now, with every ancestor, by agentWorkKey; reported by the panel. */
  agentWork: ReadonlySet<string>;
  markAgentWork: (changes: EntityChange[]) => void;
  clearAgentWork: () => void;
  pageEmbed: AssistantPageEmbed | null;
  setPageEmbed: (embed: AssistantPageEmbed | null) => void;
  /** Open the panel and post `text`: into the open conversation, or a fresh one when the panel was closed. */
  askAssistant: (text: string) => void;
  /** The pending seed, consumed by the panel. */
  seed: AssistantSeed | null;
  clearSeed: () => void;
  /** Where the panel is shown inside a page instead of floating; null floats it. */
  embedSlot: HTMLElement | null;
  setEmbedSlot: (slot: HTMLElement | null) => void;
  /** A page element stands in the launcher's corner, so the launcher steps away. */
  isLauncherHidden: boolean;
  setLauncherHidden: (hidden: boolean) => void;
}

const PlatformAssistantContext = createContext<PlatformAssistantContextValue | null>(null);

interface PlatformAssistantProviderProps {
  children: React.ReactNode;
}

export const PlatformAssistantProvider: React.FC<PlatformAssistantProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [persisted] = useState(readPanelState);
  const [isOpen, setIsOpen] = useState(persisted.isOpen);
  const [seed, setSeed] = useState<AssistantSeed | null>(null);
  const [embedSlot, setEmbedSlot] = useState<HTMLElement | null>(null);
  const [isLauncherHidden, setLauncherHidden] = useState(false);
  const [placement, setPlacement] = useState<AssistantPlacement>('docked');
  const [isDocked, setDockedOnScreen] = useState(false);
  const [sessionStart, setSessionStart] = useState<AssistantSessionStart | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [voiceState, setVoiceState] = useState<TutorState | null>(null);
  const [agentWork, setAgentWork] = useState<ReadonlySet<string>>(NO_WORK);
  const [pageEmbed, setPageEmbed] = useState<AssistantPageEmbed | null>(null);

  useEffect(() => {
    document.documentElement.classList.toggle('assistant-docked', isDocked);
  }, [isDocked]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Never reset: a cleared seed or start must not let the next one repeat a nonce the panel already consumed.
  const nonceRef = useRef(0);

  const askAssistant = useCallback((text: string) => {
    setSeed({ text, nonce: ++nonceRef.current });
    setIsOpen(true);
  }, []);

  const clearSeed = useCallback(() => setSeed(null), []);

  const startSession = useCallback((start: AssistantSessionRequest) => {
    setSessionStart({ ...start, nonce: ++nonceRef.current });
    setIsOpen(true);
  }, []);

  const clearSessionStart = useCallback(() => setSessionStart(null), []);

  const markAgentWork = useCallback((changes: EntityChange[]) => {
    const keys = changes.flatMap((change) => [change.entity, ...change.path]).map(agentWorkKey);
    setAgentWork((current) => (keys.every((key) => current.has(key)) ? current : new Set([...current, ...keys])));
  }, []);

  const clearAgentWork = useCallback(() => setAgentWork(NO_WORK), []);

  useEffect(() => {
    writePanelState({ isOpen });
  }, [isOpen]);

  // A signed-out user leaves nothing behind for whoever signs in next.
  useEffect(() => {
    if (isAuthLoading || isAuthenticated) return;
    clearAssistantStorage();
    setIsOpen(false);
    setSeed(null);
    setSessionStart(null);
  }, [isAuthenticated, isAuthLoading]);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      askAssistant,
      seed,
      clearSeed,
      embedSlot,
      setEmbedSlot,
      isLauncherHidden,
      setLauncherHidden,
      placement,
      setPlacement,
      isDocked,
      setDockedOnScreen,
      sessionStart,
      startSession,
      clearSessionStart,
      activeSessionId,
      setActiveSessionId,
      voiceState,
      setVoiceState,
      agentWork,
      markAgentWork,
      clearAgentWork,
      pageEmbed,
      setPageEmbed,
    }),
    [
      isOpen, open, close, toggle, askAssistant, seed, clearSeed, embedSlot, isLauncherHidden, placement, isDocked,
      sessionStart, startSession, clearSessionStart, activeSessionId, voiceState, agentWork, markAgentWork, clearAgentWork,
      pageEmbed,
    ]
  );

  return (
    <PlatformAssistantContext.Provider value={value}>
      {children}
    </PlatformAssistantContext.Provider>
  );
};

export const usePlatformAssistant = (): PlatformAssistantContextValue => {
  const context = useContext(PlatformAssistantContext);
  if (!context) {
    throw new Error('usePlatformAssistant must be used within a PlatformAssistantProvider');
  }
  return context;
};

export default PlatformAssistantContext;
